import styled from 'styled-components';

import { ANIM, COLORS, LOGIN, REDUCED_MOTION, SPACE } from '../../../../theme/tokens';
import { Text } from '../../../../components';
import type { PasswordStrengthLevel } from '../../../../utils/password-strength';

/** The quiet hint under the password field (shown once, save it now). */
export const FieldHint = styled(Text)`
  font-size: 12px;
  line-height: 1.5;
  color: ${COLORS.textMuted};
`;

/** The meter's slot, under the password field's message line: always there so the meter's live
 *  region has a stable home, filled only once the value is the operator's own. No reserved height:
 *  empty, it takes no room, so the hint sits directly under the field. */
export const MeterSlot = styled.div``;

const levelColor = (level: PasswordStrengthLevel): string => {
  switch (level) {
    case 'too-short':
      return LOGIN.strengthTooShort;
    case 'weak':
      return LOGIN.strengthWeak;
    case 'fair':
      return LOGIN.strengthFair;
    case 'strong':
      return LOGIN.strengthStrong;
    default: {
      const unhandled: never = level;
      return unhandled;
    }
  }
};

/**
 * The three-bar strength meter, a styled-components sibling of the one on `/register` (same LOGIN
 * tokens, same rule: every filled bar and the caption take the current level's one colour, red
 * reserved for the not-submittable too-short state).
 */
export const Meter = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACE[4]};
`;

export const Bars = styled.div`
  display: flex;
  gap: ${SPACE[4]};
`;

export const Bar = styled.span<{ $filled: boolean; $level: PasswordStrengthLevel }>`
  flex: 1;
  height: 4px;
  position: relative;
  overflow: hidden;
  border-radius: 999px;
  background: ${LOGIN.strengthTrack};

  /* The fill grows in from the left when the bar crosses its threshold; an already-filled bar only
     recolours on a level change, so typing within a level does not re-animate. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${(p) => levelColor(p.$level)};
    transform: scaleX(${(p) => (p.$filled ? 1 : 0)});
    transform-origin: left;
    transition:
      transform ${ANIM.INTERACT_MS}ms ease,
      background-color ${ANIM.INTERACT_MS}ms ease;
  }

  ${REDUCED_MOTION} {
    &::before {
      transition: none;
    }
  }
`;

export const MeterCaption = styled(Text)<{ $level: PasswordStrengthLevel }>`
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  color: ${(p) => levelColor(p.$level)};
`;
